import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useStudentAbsenceDetails from '../hooks/useStudentAbsenceDetails';
import '../../../../components/Common/styles/skeleton.css';

// Import shared components
import PageHeader from '../../../../components/Common/PageHeader';
import StudentInfoCard from '../components/StudentInfoCard';
import AbsenceHistoryTable from '../components/AbsenceHistoryTable';
import StudentDetailsSkeletonLoader from '../components/StudentDetailsSkeletonLoader';
import ErrorMessage from '../components/ErrorMessage';

const StudentAbsenceDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();
  const { details, loading, error } = useStudentAbsenceDetails(studentId ?? '');

  // Common page header configuration
  const breadcrumbItems = [
    { label: 'dashboard', path: '/dashboard' },
    { label: 'student_alerts', path: '/absences/alerts' },
    { label: 'student_details', active: true }
  ];
  

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PageHeader 
            title={t('student_absence_details')} 
            breadcrumbItems={breadcrumbItems} 
          />
          <StudentDetailsSkeletonLoader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PageHeader 
            title={t('error')} 
            breadcrumbItems={breadcrumbItems}
          />
          <ErrorMessage 
            title={t('error_loading_details')} 
            message={error} 
            icon="ti ti-alert-triangle" 
            iconColor="text-warning" 
          />
        </div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PageHeader 
            title={t('student_not_found')} 
            breadcrumbItems={breadcrumbItems} 
          />
          <ErrorMessage 
            title={t('student_not_found')} 
            message={t('student_not_found_message')} 
            icon="ti ti-user-off" 
            iconColor="text-danger" 
          />
        </div>
      </div>
    );
  }

  const { student, absenceHistory } = details;
  
  // Calculate absence statistics
  const totalHours = absenceHistory.reduce((sum, item) => sum + item.hours, 0);
  const excusedHours = absenceHistory
    .filter(item => item.type === 'excused')
    .reduce((sum, item) => sum + item.hours, 0);
  const pendingHours = absenceHistory
    .filter(item => item.type === 'pending')
    .reduce((sum, item) => sum + item.hours, 0);
  
  const excusedPercentage = totalHours > 0 ? Math.round((excusedHours / totalHours) * 100) : 0;
  const pendingPercentage = totalHours > 0 ? Math.round((pendingHours / totalHours) * 100) : 0;

  // Prepare student info card data
  const absenceStats = {
    totalHours,
    excusedHours,
    pendingHours,
    excusedPercentage,
    pendingPercentage
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader 
          title={t('student_absence_details')} 
          breadcrumbItems={breadcrumbItems} 
        />

        <div className="row">
          {/* Student Info Card */}
          <div className="col-md-4 mb-4">
            <StudentInfoCard student={student} absenceStats={absenceStats} />
          </div>

          {/* Absence History */}
          <div className="col-md-8">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title">{t('absence_history')}</h5>
              </div>
              <div className="card-body">
                <AbsenceHistoryTable absenceHistory={absenceHistory} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAbsenceDetailsPage;
